import React from 'react';
import { useLoaderData, useParams } from '@tanstack/react-router';
import {
  SurveyModel, CompletingEvent,
} from 'survey-core';
import Survey from '../survey/survey';
import {
  isApiError, isExpiredToken, isNotFoundError, isBadRequestError,
  isUnauthorizedError,
} from '../../utils/errors';
import { useStatusMessageDispatch } from '../../hooks/StatusMessageContext';
import ErrorPage from '../status-pages/error-page';
import { TokenExpirationMessage } from '../token-expiration-message/token-expiration-message';
import { displayCustomPage } from '../../utils/custom-page/display-custom-page';
import { submitAdditionalTradeReferences } from '../../api/submissions/submit-additional-trade-references';
import { getSurveyModel } from '../survey-questions/get-survey-model';
import { AdditionalTradeReferencesLoaderResponse } from '../../routes/$accountSlug/$submissionId/additional-trade-references';
import i18n from '../../i18n';

const TRADE_REFERENCES_PANEL_NAME = 'tradeReferences';

const AdditionalTradeReferences: React.FC = () => {
  const {
    settings,
    error,
  } = useLoaderData({ from: '/$accountSlug/$submissionId/additional-trade-references' }) as AdditionalTradeReferencesLoaderResponse;

  const { setStatusMessage } = useStatusMessageDispatch();
  const { accountSlug, submissionId } = useParams({ from: '/$accountSlug/$submissionId/additional-trade-references' });

  // This route is only used after token validation and redirect, so errors here are not token-related
  if (isNotFoundError(error) || isUnauthorizedError(error) || isBadRequestError(error)) {
    return <ErrorPage
      errorType={error}
      link={`/${accountSlug}/`}
           />;
  }

  const handleSubmission = async (sender: SurveyModel, options: CompletingEvent) => {
    try {
      // Additional validation for trade references count
      const tradeReferencesPanel = sender.getQuestionByName(TRADE_REFERENCES_PANEL_NAME);
      if (tradeReferencesPanel?.minPanelCount !== tradeReferencesPanel.panels.length) {
        options.allow = false;
        setStatusMessage({
          severity: 'error',
          message: `You must provide exactly ${tradeReferencesPanel?.minPanelCount} complete trade references. Currently provided: ${tradeReferencesPanel.panels.length}`,
        });
        return;
      }

      // Extract trade references data from survey responses
      const tradeReferencesResponses = sender.data;
      await submitAdditionalTradeReferences(
        accountSlug,
        submissionId,
        tradeReferencesResponses,
      );

      sender.readOnly = true;
      options.allow = true;
    } catch (err) {
      options.allow = false;
      if (isApiError(err) && isExpiredToken(err)) {
        displayCustomPage({
          survey: sender,
          component: TokenExpirationMessage,
          getProps: () => ({
            publicId: submissionId,
            slug: accountSlug,
          }),
          withoutFrame: true,
        });

        sender.readOnly = true;
      } else {
        setStatusMessage({
          severity: 'error',
          message: isApiError(err) ? err.message : 'Failed to submit additional trade references.',
        });
      }
    }
  };

  const surveyModel = getSurveyModel({
    settings,
    onCompleting: handleSubmission,
    completedHtml: i18n.t('components:pages.additional_trade_references_submitted'),
  });

  return (
    <Survey surveyModel={surveyModel} />
  );
};

export default AdditionalTradeReferences;
